// File: client/src/writer/WriteStudio.tsx 
import React, { useState, useEffect, useRef, Dispatch, SetStateAction } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { AppProvider } from "./contexts/AppContext.tsx";
import { Sidebar } from "../components/Sidebar";
import MainWorkspace from "./components/MainWorkspace";
import LoadingOverlay from "./components/LoadingOverlay";
import ImageGenerationModal from "./components/ImageGenerationModal";
import { useProjectSetupLogic } from "./hooks/useProjectSetupLogic";
import { useCoreState } from "./hooks/useCoreState";
import { useChapterState } from "./hooks/useChapterState";
import { useChapterManagement } from "./hooks/useChapterManagement";
import { useChapterAIActions } from "./hooks/useChapterAIActions";
import { useFileAndCoverActions } from "./hooks/useFileAndCoverActions";
import { useModalStates } from "./hooks/useModalStates";
import { useConfirmation } from "./hooks/useConfirmation";
import { useAppEffects } from "./hooks/useAppEffects";
import { useBookStructureManagement } from "./hooks/useBookStructureManagement";
import { ProjectState, MainView, Project, OptimizedDataLoading } from "./types";
import { loadInitialProjectState } from "./utils/projectStateUtils";
import { ErrorBoundary } from "@/components/ErrorBoundary";

interface StudioCoreProps {
    projectState: ProjectState | null;
    activeView: MainView;
    showGlobalSidebar: boolean;
    dbProject: Project | null;
    optimizedData: OptimizedDataLoading;
    onViewChange: (view: MainView) => void;
}
const StudioCore: React.FC<StudioCoreProps> = ({
    projectState,
    activeView,
    showGlobalSidebar,
    dbProject,
    optimizedData,
    onViewChange,
}) => {
    const navigate = useNavigate();
    const [initialProjectState, setInitialProjectState] =
        useState<ProjectState | null>(projectState);

    // CRITICAL FIX: Guard parent sync to prevent stale overwrites
    // Only sync from parent if parent data is fresher than current local state
    // ✅ PREVENT SYNC LOOP: Use ref to track last synced projectState and prevent circular updates
    const lastSyncedProjectStateRef = useRef<ProjectState | null>(null);

    useEffect(() => {
        // Skip if projectState hasn't actually changed from what we last synced
        if (!projectState || projectState === lastSyncedProjectStateRef.current) {
            return;
        }

        // Timestamp comparison to prevent stale overwrites
        const parentUpdatedAt = new Date((projectState as any).updatedAt || '1970-01-01').getTime();
        const currentUpdatedAt = new Date((initialProjectState as any)?.updatedAt || '1970-01-01').getTime();
        const timeDiffSeconds = (parentUpdatedAt - currentUpdatedAt) / 1000;

        // console.log('🔄 StudioCore: Parent sync evaluation:', {
        //     parentTimestamp: projectState.updatedAt || 'No timestamp',
        //     currentTimestamp: initialProjectState?.updatedAt || 'No timestamp',
        //     parentUpdatedAtMs: parentUpdatedAt,
        //     currentUpdatedAtMs: currentUpdatedAt,
        //     timeDifferenceSeconds: timeDiffSeconds,
        //     parentIsNewer: parentUpdatedAt > currentUpdatedAt
        // });

        // Only sync if parent data is strictly newer (avoid equal-timestamp stale overwrites)
        const parentIsNewer = parentUpdatedAt > currentUpdatedAt;

        // For equal timestamps, compare bookMetadata content to detect stale overwrites
        let shouldSync = parentIsNewer;
        if (parentUpdatedAt === currentUpdatedAt) {
            // Create stable hash function for content comparison
            const stableHash = (obj: any) => {
                if (!obj) return 'null';
                try {
                    // Sort keys for consistent stringification
                    const sorted = Object.keys(obj).sort().reduce((result: any, key) => {
                        result[key] = obj[key];
                        return result;
                    }, {});
                    return JSON.stringify(sorted);
                } catch {
                    return 'error';
                }
            };

            const parentHash = stableHash(projectState.bookMetadata);
            const currentHash = stableHash(initialProjectState?.bookMetadata);
            const contentDiffers = parentHash !== currentHash;

            // console.log('🔍 StudioCore: Equal timestamps detected, checking content hash:', {
            //     contentDiffers,
            //     parentBookMetaExists: !!projectState.bookMetadata,
            //     currentBookMetaExists: !!initialProjectState?.bookMetadata,
            //     parentHashPreview: parentHash.substring(0, 50) + '...',
            //     currentHashPreview: currentHash.substring(0, 50) + '...'
            // });

            // Only sync on equal timestamp if parent has genuinely different content
            shouldSync = contentDiffers;
        }

        if (shouldSync) {
            lastSyncedProjectStateRef.current = projectState; // Track what we synced
            setInitialProjectState(projectState);
        } else {
        }
    }, [projectState]); // ✅ Remove initialProjectState from deps to prevent loop

    const editor = useEditor({
        extensions: [StarterKit],
        content: "",
    });

    const coreState = useCoreState(
        initialProjectState,
        setInitialProjectState,
        editor,
        dbProject,
        optimizedData.blueprint, // Pass blueprint state management
    );
    const {
        projectState: coreProjectState,
        activeChapter,
        mainView: internalMainView,
        isInitialBlueprintReady,
        apiKey,
        setMainView,
    } = coreState;

    // Removed excessive console logging to prevent input lag

    useEffect(() => {
        if (activeView && activeView !== internalMainView) {
            setMainView(activeView);
        }
    }, [activeView, internalMainView, setMainView]);

    const chapterState = useChapterState(
        activeChapter || undefined,
        internalMainView,
        isInitialBlueprintReady,
    );
    const modalStates = useModalStates(apiKey);

    const projectManagement = {
        saveProject: () => Promise.resolve(),
        deleteProject: () => Promise.resolve(),
        isLoading: coreState.isLoading.save || coreState.isLoading.delete,
        confirmDiscardProjectChanges: coreState.confirmOverallDiscardChanges,
    };

    const confirmationControls = useConfirmation(
        projectManagement,
        chapterState.areChapterDetailsCurrentlyUnsaved,
        isInitialBlueprintReady,
        internalMainView,
        chapterState.activeChapterDetailEdits,
    );

    const fileAndCoverActions = useFileAndCoverActions(
        coreState,
        modalStates,
        confirmationControls,
    );

    const chapterManagement = useChapterManagement(
        coreState,
        modalStates,
        confirmationControls,
    );

    const chapterAIActions = useChapterAIActions(
        coreState,
        chapterState,
        modalStates,
        editor,
    );

    const bookStructureManagement = useBookStructureManagement(coreState, modalStates);

    const handleExportChapterContentPreferenceChange = (value: 'preview' | 'content') => {
        coreState.setProjectState(prevState => prevState ? ({
            ...prevState,
            exportChapterContentPreference: value,
            isUnsaved: true,
        }) : prevState);
    };

    // BLUEPRINT MIGRATION: Use blueprint.worldBuildingSuggestions instead of worldBuildingData
    // PHASE 6 FIX: Provide fallback to prevent undefined access crash
    const worldBuildingData = coreState.projectState?.blueprint?.worldBuildingSuggestions || {
        characters: [],
        locations: [],
        customCategories: [],
        logLine: '',
        selectedStoryTitle: '',
        storyTitleIdeas: [],
        storyStartConcept: '',
        storyEndConcept: ''
    };
    
    const projectSetupLogic = useProjectSetupLogic(coreState, modalStates, onViewChange);

    // CRITICAL FIX: Call useAppEffects to handle content loading
    useAppEffects(coreState, chapterState, projectManagement as any, coreState.determineInitialViewBasedOnState as any);

    const contextValue = {
        ...coreState,
        projectState: coreProjectState,
        editor,
        mainView: internalMainView, // Pass down the synced internal view
        chapterManagement,
        chapterAIActions,
        fileAndCoverActions,
        modalStates,
        confirmationControls,
        chapterState,
        projectSetupLogic,
        setMainView,
        hasInitialBlueprint: coreState.isInitialBlueprintReady,
        optimizedData, // Add optimized data to context

        openImageGenerationModalFromContext: modalStates.openImageGenerationModal,
        handleExportChapterContentPreferenceChange: handleExportChapterContentPreferenceChange,
        handleGenerateIndividualBookStructureTextElement: bookStructureManagement.handleGenerateIndividualBookStructureTextElement,
    };

    // views that actually need the TipTap editor
    const editorRequiredViews: MainView[] = [
        "chaptersBuilding",
        "bookPreview", // reader mode uses editor HTML
        "bookStructureStudio", // optional – remove if not required
    ];

    // ✅ LENIENT GATE: Only block if editor is missing for editor-required views
    // (projectState will be initialized by coreState if missing)
    if (editorRequiredViews.includes(internalMainView) && !editor) {
        return <LoadingOverlay message="Loading editor..." />;
    }

    return (
        <AppProvider value={contextValue}>
            <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
                {showGlobalSidebar && <Sidebar />}
                <MainWorkspace />
                {Object.values(coreState.isLoading).some(Boolean) && (
                    <LoadingOverlay />
                )}
            </div>
            <ImageGenerationModal />
        </AppProvider>
    );
};

interface WriteStudioProps {
    projectId: string | undefined;
    activeView: MainView;
    onViewChange: (view: MainView) => void;
    project: Project | null;
    projectState: ProjectState | null;
    setProjectState: Dispatch<SetStateAction<ProjectState | null>>;
    optimizedData: OptimizedDataLoading;
    /**
     * Whether to show the global sidebar navigation.
     * Defaults to true.
     */
    showGlobalSidebar?: boolean;
}

const WriteStudio: React.FC<WriteStudioProps> = ({
    projectId,
    activeView,
    onViewChange,
    project,
    projectState,
    setProjectState,
    optimizedData,
    showGlobalSidebar = true,
}) => {
    console.log('🔍 TRACE: WriteStudio called with props:', {
        projectId,
        activeView,
        project: project ? { id: project.id, uuid: project.uuid, title: project.title } : null,
        projectState: projectState ? 'exists' : 'null',
        optimizedData: optimizedData ? 'exists' : 'null'
    });

    // ✅ LENIENT GATE: Only require project data (projectState will be initialized in StudioCore)
    if (!project) {
        return <LoadingOverlay message="Loading project data..." />;
    }

    return (
        <DndProvider backend={HTML5Backend}>
            <StudioCore
                projectState={projectState}
                activeView={activeView}
                showGlobalSidebar={showGlobalSidebar}
                dbProject={project}
                optimizedData={optimizedData}
                onViewChange={onViewChange}
            />
        </DndProvider>
    );
};

// ✅ CRITICAL FIX: Wrap with React.memo to prevent unnecessary re-renders
export default React.memo(WriteStudio, (prevProps, nextProps) => {
    // Return true if props are equal (should NOT re-render)
    // Return false if props are different (should re-render)

    const projectEqual = prevProps.project?.uuid === nextProps.project?.uuid &&
                        (prevProps.project as any)?.updatedAt === (nextProps.project as any)?.updatedAt;

    const projectStateEqual = (prevProps.projectState as any)?.updatedAt === (nextProps.projectState as any)?.updatedAt &&
                             prevProps.projectState?.currentProjectId === nextProps.projectState?.currentProjectId;

    const optimizedDataEqual = prevProps.optimizedData === nextProps.optimizedData; // Reference equality since it's memoized

    const otherPropsEqual = prevProps.projectId === nextProps.projectId &&
                           prevProps.activeView === nextProps.activeView &&
                           prevProps.showGlobalSidebar === nextProps.showGlobalSidebar;

    const shouldNotRerender = projectEqual && projectStateEqual && optimizedDataEqual && otherPropsEqual;

    if (!shouldNotRerender) {
        console.log('🔄 WriteStudio re-rendering due to prop changes:', {
            projectChanged: !projectEqual,
            projectStateChanged: !projectStateEqual,
            optimizedDataChanged: !optimizedDataEqual,
            otherPropsChanged: !otherPropsEqual
        });
    }

    return shouldNotRerender;
});
